import React from 'react';
import { useTranslation } from 'react-i18next';
import { Filter, X } from 'lucide-react';
import { Brand, MentionFilters as Filters } from '../../services/listeningService';
import Button from '../ui/Button';

interface MentionFiltersProps {
  filters: Filters;
  brands: Brand[];
  onChange: (filters: Filters) => void;
  onClear: () => void;
}

const MentionFiltersComponent: React.FC<MentionFiltersProps> = ({
  filters,
  brands,
  onChange,
  onClear
}) => {
  const { t } = useTranslation();

  const platforms = [
    { value: 'twitter', label: 'Twitter/X' },
    { value: 'instagram', label: 'Instagram' },
    { value: 'facebook', label: 'Facebook' },
    { value: 'linkedin', label: 'LinkedIn' },
    { value: 'youtube', label: 'YouTube' },
    { value: 'reddit', label: 'Reddit' },
    { value: 'news', label: t('listening.filters.news', 'News') },
    { value: 'blog', label: t('listening.filters.blogs', 'Blogs') }
  ];

  const sentiments = [
    { value: 'positive', label: t('listening.filters.positive', 'Positive') },
    { value: 'negative', label: t('listening.filters.negative', 'Negative') },
    { value: 'neutral', label: t('listening.filters.neutral', 'Neutral') }
  ];

  const hasActiveFilters = Object.values(filters).some(
    (v) => v !== undefined && v !== '' && v !== null
  );

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2 text-gray-700 dark:text-gray-300">
          <Filter className="w-4 h-4" />
          <span className="font-medium">{t('listening.filters.title', 'Filters')}</span>
        </div>
        {hasActiveFilters && (
          <Button variant="ghost" size="sm" onClick={onClear} className="text-xs">
            <X className="w-3 h-3 mr-1" />
            {t('listening.filters.clear', 'Clear')}
          </Button>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Brand Filter */}
        <div>
          <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
            {t('listening.filters.brand', 'Brand')}
          </label>
          <select
            value={filters.brand_type ? `__${filters.brand_type}__` : filters.brand_id || ''}
            onChange={(e) => {
              const value = e.target.value;
              if (value === '__own__') {
                onChange({ ...filters, brand_id: undefined, brand_type: 'own' });
              } else if (value === '__competitor__') {
                onChange({ ...filters, brand_id: undefined, brand_type: 'competitor' });
              } else {
                onChange({ ...filters, brand_id: value || undefined, brand_type: undefined });
              }
            }}
            className="w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            <option value="">{t('listening.filters.allBrands', 'All')}</option>
            <option value="__own__">📍 {t('listening.filters.myBrands', 'My brands')}</option>
            <option value="__competitor__">🎯 {t('listening.filters.competitors', 'Competitors')}</option>
            <optgroup label={t('listening.filters.myBrands', 'My brands')}>
              {brands.filter(b => b.type === 'own').map((brand) => (
                <option key={brand.id} value={brand.id}>
                  {brand.name}
                </option>
              ))}
            </optgroup>
            <optgroup label={t('listening.filters.competitors', 'Competitors')}>
              {brands.filter(b => b.type === 'competitor').map((brand) => (
                <option key={brand.id} value={brand.id}>
                  {brand.name}
                </option>
              ))}
            </optgroup>
          </select>
        </div>

        {/* Platform Filter */}
        <div>
          <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
            {t('listening.filters.platform', 'Platform')}
          </label>
          <select
            value={filters.platform || ''}
            onChange={(e) =>
              onChange({ ...filters, platform: e.target.value || undefined })
            }
            className="w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            <option value="">{t('listening.filters.allPlatforms', 'All')}</option>
            {platforms.map((p) => (
              <option key={p.value} value={p.value}>
                {p.label}
              </option>
            ))}
          </select>
        </div>

        {/* Sentiment Filter */}
        <div>
          <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
            {t('listening.filters.sentiment', 'Sentiment')}
          </label>
          <select
            value={filters.sentiment || ''}
            onChange={(e) =>
              onChange({
                ...filters,
                sentiment: (e.target.value as 'positive' | 'negative' | 'neutral') || undefined
              })
            }
            className="w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            <option value="">{t('listening.filters.allSentiments', 'All')}</option>
            {sentiments.map((s) => (
              <option key={s.value} value={s.value}>
                {s.label}
              </option>
            ))}
          </select>
        </div>

        {/* Response Filter */}
        <div>
          <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
            Status
          </label>
          <select
            value={
              filters.responded === true
                ? 'responded'
                : filters.requires_response === true
                ? 'pending'
                : ''
            }
            onChange={(e) => {
              const value = e.target.value;
              if (value === 'responded') {
                onChange({ ...filters, responded: true, requires_response: undefined });
              } else if (value === 'pending') {
                onChange({ ...filters, requires_response: true, responded: false });
              } else {
                onChange({ ...filters, responded: undefined, requires_response: undefined });
              }
            }}
            className="w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            <option value="">{t('listening.filters.allStatuses', 'All')}</option>
            <option value="pending">{t('listening.filters.requiresResponse', 'Requires response')}</option>
            <option value="responded">{t('listening.filters.responded', 'Responded')}</option>
          </select>
        </div>
      </div>

      {/* Date Range */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4 pt-4 border-t border-gray-100 dark:border-gray-700">
        <div>
          <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
            {t('listening.filters.startDate', 'Start date')}
          </label>
          <input
            type="date"
            value={filters.start_date?.split('T')[0] || ''}
            onChange={(e) =>
              onChange({
                ...filters,
                start_date: e.target.value ? new Date(e.target.value).toISOString() : undefined
              })
            }
            className="w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
            {t('listening.filters.endDate', 'End date')}
          </label>
          <input
            type="date"
            value={filters.end_date?.split('T')[0] || ''}
            onChange={(e) =>
              onChange({
                ...filters,
                end_date: e.target.value ? new Date(e.target.value).toISOString() : undefined
              })
            }
            className="w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>
      </div>

      {/* Influencer Toggle */}
      <div className="mt-4 pt-4 border-t border-gray-100 dark:border-gray-700">
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={filters.is_influencer === true}
            onChange={(e) =>
              onChange({
                ...filters,
                is_influencer: e.target.checked ? true : undefined
              })
            }
            className="w-4 h-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
          />
          <span className="text-sm text-gray-700 dark:text-gray-300">
            {t('listening.filters.onlyInfluencers', 'Only influencers (+10K followers)')}
          </span>
        </label>
      </div>
    </div>
  );
};

export default MentionFiltersComponent;
